import { useRef, useState } from 'react';

const API = import.meta.env.VITE_API_URL || '/api';

export interface Attachment {
  id: string;
  poId: string;
  fileName: string;
  filePath: string;
  fileType: string;
  fileSize: number | null;
  category: string;
  createdAt: string;
}

interface Props {
  /** Si está presente sube directo al server; si no, entrega el File al padre (p.ej. crear orden) */
  poId?: string;
  category?: string;
  label?: string;
  onUploaded?: (att: Attachment) => void;
  onFileSelected?: (file: File | null) => void;
  onError?: (msg: string) => void;
}

export function AttachmentPicker({ poId, category = 'order', label = 'Adjuntar archivo', onUploaded, onFileSelected, onError }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ name: string; ok: boolean } | null>(null);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-subir el mismo archivo
    if (!file) return;

    const isImg = file.type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/i.test(file.name);
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (!isImg && !isPdf) {
      onError?.('Solo se permiten imágenes (PNG, JPG, GIF, WEBP, BMP) o PDF');
      return;
    }

    // Modo deferred: la orden aún no existe (form de creación)
    if (!poId) {
      setResult({ name: file.name, ok: true });
      onFileSelected?.(file);
      return;
    }

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/purchase-orders/${poId}/attachments?category=${category}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
          'x-tenant-slug': localStorage.getItem('tenantSlug') || '',
        },
        body: fd,
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Error al subir el archivo');
      setResult({ name: file.name, ok: true });
      onUploaded?.(d.data);
    } catch (err: any) {
      setResult({ name: file.name, ok: false });
      onError?.(err.message || 'Error al subir el archivo');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60 min-h-[44px] transition-colors"
      >
        <span aria-hidden>{busy ? '⏳' : '📎'}</span>
        {busy ? 'Subiendo...' : label}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/bmp,application/pdf,.pdf"
        className="hidden"
        onChange={handleFile}
      />
      {result && (
        <span
          className={`text-sm truncate max-w-[220px] ${
            result.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
          }`}
          title={result.name}
        >
          {result.ok ? `✓ ${result.name}` : `✗ ${result.name} (no se pudo subir)`}
        </span>
      )}
    </div>
  );
}